import { FC, useMemo, useState } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { Organization } from "@models/organization.model.ts";

interface Props {
  allOrgs: Organization[];
  excludeIds: Set<number>;
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  placeholder?: string;
  showTopLevelOption?: boolean;
  topLevelLabel?: string;
  topLevelHelp?: string;
  emptyMessage?: string;
  maxHeight?: number;
}

const OrgPickerList: FC<Props> = ({
  allOrgs,
  excludeIds,
  selectedId,
  onSelect,
  placeholder = "Search organizations…",
  showTopLevelOption = false,
  topLevelLabel = "— Top-level —",
  topLevelHelp = "Remove parent. This org will be a root organization.",
  emptyMessage = "No matching organizations.",
  maxHeight = 320,
}) => {
  const [query, setQuery] = useState("");

  const candidates = useMemo(() => {
    return allOrgs
      .filter((o) => !excludeIds.has(o.id))
      .filter((o) => o.name.toLowerCase().includes(query.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allOrgs, excludeIds, query]);

  return (
    <>
      <InputGroup className="mb-3">
        <InputGroup.Text className="bg-white">
          <i className="bi bi-search text-muted" />
        </InputGroup.Text>
        <Form.Control
          autoFocus
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="border-start-0"
        />
      </InputGroup>

      {showTopLevelOption && (
        <div
          className={`d-flex align-items-center justify-content-between p-2 rounded mb-2 ${
            selectedId === null ? "bg-primary bg-opacity-10" : ""
          }`}
          style={{ cursor: "pointer", border: "1px dashed #dbdfe1" }}
          onClick={() => onSelect(null)}
        >
          <div>
            <div className="fw-semibold">{topLevelLabel}</div>
            <div className="text-muted small">{topLevelHelp}</div>
          </div>
          {selectedId === null && <i className="bi bi-check-circle-fill text-primary" />}
        </div>
      )}

      <div style={{ maxHeight, overflowY: "auto" }}>
        {candidates.length === 0 && (
          <div className="text-center text-muted py-4 small">{emptyMessage}</div>
        )}
        {candidates.map((o) => (
          <div
            key={o.id}
            className={`d-flex align-items-center justify-content-between p-2 rounded ${
              selectedId === o.id ? "bg-primary bg-opacity-10" : ""
            }`}
            style={{ cursor: "pointer" }}
            onClick={() => onSelect(o.id)}
          >
            <div>
              <div className="fw-semibold">{o.name}</div>
              {o.description && <div className="text-muted small">{o.description}</div>}
            </div>
            {selectedId === o.id && <i className="bi bi-check-circle-fill text-primary" />}
          </div>
        ))}
      </div>
    </>
  );
};

export default OrgPickerList;
